import { useState } from "react";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { declineIncomingPatientRequestApi } from "@/portals/patient/api/familyApi";
import type { IncomingPatientAccessRequest } from "@/shared/types/family";
import { ApproveCaregiverAccessModal } from "./ApproveCaregiverAccessModal";

interface AccessRequestsBannerProps {
  requests: IncomingPatientAccessRequest[];
  /** Refetches dependents/caregivers/incoming-requests — called after a successful accept/decline. */
  onChange: () => void;
}

interface AccessRequestCardProps {
  request: IncomingPatientAccessRequest;
  isBusy: boolean;
  onAccept: () => void;
  onDecline: () => void;
  onDismiss: () => void;
}

// Same dark-navy "quick card" as the caregiver dashboard's InviteBanner (see
// CaregiverDashboardView.tsx) — this is the mirror-image direction: a
// caregiver asking to view THIS patient's records, instead of a patient
// having already shared theirs. Deliberately the same visual language on
// both sides of the CareRelationship, since it's the same underlying "you
// have an unreviewed access request" concept either way.
function AccessRequestCard({ request, isBusy, onAccept, onDecline, onDismiss }: AccessRequestCardProps) {
  return (
    <div className="relative mb-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl bg-[#180047] px-5 py-4 pr-11">
      <Button
        variant="ghost"
        onClick={onDismiss}
        aria-label={`Hide access request from ${request.caregiverName}`}
        title="Hide from dashboard"
        className="!absolute !right-2.5 !top-2.5 !flex !h-7 !w-7 !items-center !justify-center !rounded-full !p-0 !text-white/70 hover:!bg-white/10 hover:!text-white"
      >
        <Icon icon="lucide:x" className="h-4 w-4 !text-current" />
      </Button>
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#F9CED8]">
          <Icon icon="lucide:user-round-search" className="h-5 w-5 text-[#180047]" />
        </div>
        <div>
          <h2 className="text-sm font-extrabold !text-white">{request.caregiverName} wants access to your health records</h2>
          <p className="mt-0.5 text-xs text-[#C7C2D6]">
            You choose what to share — nothing is granted until you approve.
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Button
          variant="outline"
          onClick={onDecline}
          disabled={isBusy}
          className="!h-auto !rounded-lg !border-white/30 !bg-transparent !px-4 !py-2 !text-sm !font-semibold !text-white hover:!border-white/30 hover:!bg-white/10 hover:!text-white"
        >
          Decline
        </Button>
        <Button
          onClick={onAccept}
          disabled={isBusy}
          loading={isBusy}
          className="!h-auto !rounded-lg !border-transparent !bg-[#F27F95] !px-4 !py-2 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#EF667F] hover:!text-white"
        >
          Accept
        </Button>
      </div>
    </div>
  );
}

/**
 * Shows every Direction-A caregiver-initiated request still awaiting this
 * patient's review (see FamilyContext.incomingPatientRequests). Approving
 * grants every record category by default (the caregiver never picks any
 * up front) — narrowable afterward via the existing Manage Access flow.
 */
export function AccessRequestsBanner({ requests, onChange }: AccessRequestsBannerProps) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());
  const [requestBeingApproved, setRequestBeingApproved] = useState<IncomingPatientAccessRequest | null>(null);
  const visibleRequests = requests.filter((request) => !dismissedIds.has(request.id));

  if (visibleRequests.length === 0) return null;

  const handleDecline = async (request: IncomingPatientAccessRequest) => {
    setBusyId(request.id);
    const { error } = await declineIncomingPatientRequestApi(request.id);
    setBusyId(null);
    if (error) {
      toastError(error);
      return;
    }
    onChange();
    toastSuccess("Request declined.");
  };

  return (
    <>
      <div className="mx-4 mt-4 lg:mx-6">
        {visibleRequests.map((request) => (
          <AccessRequestCard
            key={request.id}
            request={request}
            isBusy={busyId === request.id}
            onAccept={() => setRequestBeingApproved(request)}
            onDecline={() => handleDecline(request)}
            onDismiss={() => setDismissedIds((current) => new Set(current).add(request.id))}
          />
        ))}
      </div>
      <ApproveCaregiverAccessModal
        open={requestBeingApproved !== null}
        request={requestBeingApproved}
        onOpenChange={(open) => { if (!open) setRequestBeingApproved(null); }}
        onApproved={() => {
          if (requestBeingApproved) {
            setDismissedIds((current) => new Set(current).add(requestBeingApproved.id));
          }
          setRequestBeingApproved(null);
          onChange();
        }}
      />
    </>
  );
}
